'use client';

import React, { useState } from 'react';
import Image from 'next/image';

interface TokenImageProps {
  symbol: string;
  address?: string;
  name?: string;
  imageUrl?: string;
  ogImage?: string;
  size?: number;
  className?: string;
}

export default function TokenImage({ 
  symbol, 
  address, 
  name,
  imageUrl,
  ogImage,
  size = 32, 
  className = '' 
}: TokenImageProps) {
  const [imageError, setImageError] = useState<Record<string, boolean>>({});
  const [currentSrc, setCurrentSrc] = useState(0);

  // Multiple image sources in order of preference - prioritize our API data
  const imageSources = [
    // Use our own API image data first
    imageUrl,
    ogImage,
    // Fallback to external sources if our data isn't available
    `https://assets.coingecko.com/coins/images/small/${symbol.toLowerCase()}.png`,
    `https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/assets/${address}/logo.png`,
    `https://s2.coinmarketcap.com/static/img/coins/64x64/${symbol.toLowerCase()}.png`,
    // Generic token icon as ultimate fallback
    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTYiIGN5PSIxNiIgcj0iMTYiIGZpbGw9IiNGM0Y0RjYiLz4KPHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4PSI4IiB5PSI4Ij4KPHBhdGggZD0iTTggMTRBNiA2IDAgMSAwIDggMkE2IDYgMCAwIDAgOCAxNFoiIGZpbGw9IiM2QjczODAiLz4KPHBhdGggZD0iTTUuNSA2SDEwLjVMMTAuNSA4SDguNVY5LjVIMTBWMTAuNUg4LjVWMTJINy41VjEwLjVINlY5LjVINy41VjhINS1WNloiIGZpbGw9IndoaXRlIi8+Cjwvc3ZnPgo8L3N2Zz4KPC9zdmc+'
  ].filter(Boolean); // Remove undefined/null values


  const handleImageError = () => {
    const nextSrc = currentSrc + 1;
    if (nextSrc < imageSources.length) {
      setCurrentSrc(nextSrc);
    } else {
      // All sources failed, show fallback
      setImageError({ [symbol]: true });
    }
  };

  // Fallback component with token symbol
  const TokenFallback = () => (
    <div 
      className={`flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 rounded-full text-white font-bold ${className}`}
      style={{ width: size, height: size, fontSize: Math.max(10, size * 0.4) }}
    >
      {symbol.charAt(0).toUpperCase()}
    </div>
  );

  // If all image sources failed, show fallback
  if (imageError[symbol] || currentSrc >= imageSources.length - 1) {
    return <TokenFallback />;
  }

  return (
    <div className={`relative ${className}`} style={{ width: size, height: size }}>
      <Image
        src={imageSources[currentSrc] || ''}
        alt={`${symbol} token logo`}
        width={size}
        height={size}
        className="rounded-full"
        onError={handleImageError}
        onLoad={() => {
          // Reset error state if image loads successfully
          if (imageError[symbol]) {
            setImageError({ ...imageError, [symbol]: false });
          }
        }}
        unoptimized // Allow external domains without next.config.js changes
      />
      
      {/* Loading fallback while image loads */}
      <div 
        className="absolute inset-0 bg-gray-200 rounded-full animate-pulse"
        style={{ 
          display: imageError[symbol] ? 'none' : 'block',
          zIndex: -1 
        }}
      />
    </div>
  );
}

// Hook for getting token image URL (useful for other components)
export const useTokenImage = (symbol: string, address?: string) => {
  const getTokenImageUrl = () => {
    // Return the primary source
    return `https://assets.coingecko.com/coins/images/small/${symbol.toLowerCase()}.png`;
  };

  return { getTokenImageUrl };
};
